import { Container, OrderableItems } from "@/components";
import { useDynamicFormContext } from "@/components/DynamicForm/DynamicFormProvider";
import { FormsHeader } from "@/components/PageHeader";
import { EntityStatus, RIGHTS } from "@/enums";
import {
  getActiveField,
  getFormData,
  routes,
  updateFormFieldsRecursive,
  useTableExpandAll,
} from "@/helpers";
import {
  useDeleteFormPropertyMutation,
  useGetAdminFormsQuery,
  useGetUserTokenQuery,
  useReorderPropertiesMutation,
} from "@/services";
import {
  faBarsSort,
  faCirclePlus,
  faEdit,
  faEye,
  faEyeSlash,
  faSquareMinus,
  faSquarePlus,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Col,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { func, string } from "prop-types";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { sortRecursively } from "./helper/sortRecursively";

const { Title, Paragraph } = Typography;
const { Option } = Select;

export const EditFormContent = ({
  productCategory,
  setProductCategory,
  formName,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { formType } = useParams();
  const { getDependentPropertyField } = useDynamicFormContext();

  const [ordering, setOrdering] = useState(false);
  const [formData, setFormData] = useState([]);
  const [hideInactiveFields, setHideInactiveFields] = useState(false);

  const hasDefaultExpanded = useRef(false);

  const { expandAll, toggleAllExpanded, isAllExpanded, tableProps } =
    useTableExpandAll(formData, (field) => field.property.id, "children");

  const { data: userToken } = useGetUserTokenQuery();

  const { data: { forms, categories } = {}, isFetching } =
    useGetAdminFormsQuery();

  const [deleteFormProperty] = useDeleteFormPropertyMutation();
  const [reorderProperties, { isLoading: isOrderingSaving }] =
    useReorderPropertiesMutation();

  useEffect(() => {
    setOrdering(isOrderingSaving);
  }, [isOrderingSaving]);

  useEffect(() => {
    if (!forms) return;

    setFormData(getFormData(forms?.[formName], hideInactiveFields));
  }, [formName, forms, hideInactiveFields]);

  useEffect(() => {
    const pageHasLoaded = formData?.length && !hasDefaultExpanded.current;

    if (pageHasLoaded) {
      hasDefaultExpanded.current = expandAll();
    }
  }, [expandAll, formData]);

  const createExpandAllTitle = () => {
    const fieldName = intl.formatMessage({ id: "editForm.colFieldLabel" });
    const sharedProps = {
      onClick: toggleAllExpanded,
      style: { paddingRight: "5px" },
    };

    if (isAllExpanded)
      return (
        <>
          <FontAwesomeIcon
            icon={faSquareMinus}
            data-test-id="edit-form-toggle-all-minus"
            {...sharedProps}
          />
          {fieldName}
        </>
      );

    return (
      <>
        <FontAwesomeIcon
          icon={faSquarePlus}
          data-test-id="edit-form-toggle-all-plus"
          {...sharedProps}
        />
        {fieldName}
      </>
    );
  };

  const columns = [
    {
      title: createExpandAllTitle(),
      key: "fieldLabel",
      dataIndex: "property",
      render: (field) => field?.label?.fallback,
    },
    {
      title: intl.formatMessage({ id: "editForm.colFieldName" }),
      dataIndex: "property",
      className: "drag-visible",
      render: (field) => field?.name,
    },
    {
      title: intl.formatMessage({ id: "editForm.colFieldType" }),
      key: "fieldType",
      dataIndex: "property",
      render: (field) =>
        intl.formatMessage({
          id: `enum.fieldType.${field.fieldType?.toLowerCase()}`,
          defaultMessage: field.fieldType || "Undefined",
        }),
    },
    {
      title: intl.formatMessage({ id: "editForm.colCategory" }),
      key: "category",
      dataIndex: "category",
      render: (field) =>
        field
          ? intl.formatMessage({
              id: field.label.id,
              defaultMessage: field.label.fallback,
            })
          : "",
    },
    {
      title: intl.formatMessage({ id: "editForm.colStatus" }),
      key: "entityStatus",
      dataIndex: "property",
      render: (field) => {
        let color;
        let entityStatus;
        switch (field.status) {
          case EntityStatus.Inactive:
            color = "volcano";
            entityStatus = EntityStatus.Inactive;

            break;
          case EntityStatus.Preview:
            color = "geekblue";
            entityStatus = EntityStatus.Preview;

            break;
          case EntityStatus.Active:
          default:
            color = "green";
            entityStatus = EntityStatus.Active;

            break;
        }

        return (
          <Tag color={color}>
            <FormattedMessage
              id={`enum.entityStatus.${entityStatus.toLowerCase()}`}
            />
          </Tag>
        );
      },
    },
    {
      title: intl.formatMessage({ id: "general.actions" }),
      dataIndex: "property",
      key: "actions",
      render: (field, record) => (
        <Space>
          <Button
            onClick={() =>
              navigate(
                routes.admin.forms.field.newDependent(formType, field.id)
              )
            }
            data-test-id="edit-form-add-dependent"
            icon={<FontAwesomeIcon icon={faCirclePlus} />}
          >
            {intl.formatMessage({ id: "editForm.addDependentFieldButton" })}
          </Button>
          <Link to={routes.admin.forms.field.get(formType, field.id)}>
            <Button
              data-test-id="edit-form-edit"
              icon={<FontAwesomeIcon icon={faEdit} />}
            />
          </Link>
          {record.canDelete && (
            <Popconfirm
              title={intl.formatMessage({ id: "editForm.actionDelete" })}
              okText={intl.formatMessage({ id: "general.delete" })}
              cancelText={intl.formatMessage({ id: "general.cancel" })}
              onConfirm={() => handleDeleteField(field)}
            >
              <Button
                data-test-id="edit-form-delete"
                icon={<FontAwesomeIcon icon={faTrash} />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
    {
      title: intl.formatMessage({ id: "editForm.colDependentValues" }),
      key: "dependent-values",
      render: (field) => {
        const {
          property: {
            dependsOnPropertyId: parentId,
            dependsOnPropertyValues: parentValues,
          } = {},
        } = field;

        if (!parentId || !parentValues) return;

        const fieldValues = {};

        getDependentPropertyField(parentId)?.values.forEach((field) => {
          fieldValues[field.id] = field.label.fallback;
        });

        if (!Object.keys(fieldValues).length) return;

        return parentValues.map((id) => <Tag key={id}>{fieldValues[id]}</Tag>);
      },
    },
  ];

  const handleDeleteField = (field) => {
    deleteFormProperty({ form: formName, property: field })
      .unwrap()
      .then(() => {
        toast.success(
          intl.formatMessage(
            {
              id: "editForm.fieldDeleted",
            },
            { field: field.name }
          )
        );
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        );
      });
  };

  const handleReorderingSave = () => {
    const sortedFormData = sortRecursively(formData);

    reorderProperties({ form: formName, order: sortedFormData })
      .unwrap()
      .then(() => {
        toast.success(intl.formatMessage({ id: "editForm.sortSaved" }));
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        );
      });
  };
  const handleSortCancel = () => {
    if (hideInactiveFields) {
      const activeFormFields = forms[formName]
        .map((form) => getActiveField(form))
        .filter((form) => !!form);

      setFormData(activeFormFields);
    } else {
      setFormData(forms[formName]);
    }

    setOrdering(false);
  };

  const sortingHeaderActions = [
    <Button
      data-test-id="edit-form-cancel-sort"
      type="default"
      onClick={handleSortCancel}
      key="sort-cancel"
    >
      {intl.formatMessage({ id: "general.cancel" })}
    </Button>,
    <Button
      data-test-id="edit-form-save-sort"
      type="primary"
      onClick={handleReorderingSave}
      loading={isOrderingSaving}
      key="sort-save"
    >
      {intl.formatMessage({ id: "general.save" })}
    </Button>,
  ];

  const defaultHeaderActions = [
    <Button
      data-test-id="edit-form-toggle-inactive"
      type="default"
      key="toggleInactive"
      onClick={() => setHideInactiveFields(!hideInactiveFields)}
      icon={<FontAwesomeIcon icon={hideInactiveFields ? faEye : faEyeSlash} />}
    >
      {hideInactiveFields
        ? intl.formatMessage({ id: "editForm.showInactiveFields" })
        : intl.formatMessage({ id: "editForm.hideInactiveFields" })}
    </Button>,
    <Button
      data-test-id="edit-form-sort"
      type="default"
      onClick={() => {
        setHideInactiveFields(false);
        setOrdering(true);
      }}
      icon={<FontAwesomeIcon icon={faBarsSort} />}
      key="begin-sort"
    >
      {intl.formatMessage({ id: "general.reorder" })}
    </Button>,
    <Button
      onClick={() => navigate(routes.admin.forms.field.new(formType))}
      data-test-id="edit-form-add-field"
      type="primary"
      icon={<FontAwesomeIcon icon={faCirclePlus} />}
      key="add-field"
    >
      {intl.formatMessage({ id: "editForm.addFieldButton" })}
    </Button>,
  ];

  return (
    <>
      <FormsHeader
        extra={ordering ? sortingHeaderActions : defaultHeaderActions}
      />
      <Container>
        <Typography>
          <Title level={2}>
            {intl.formatMessage(
              { id: "editForm.contentTitle" },
              {
                formType: intl.formatMessage({
                  id: `enum.formType.${formType}`,
                }),
              }
            )}
          </Title>
          <Paragraph>
            {intl.formatMessage({ id: "editForm.contentSubTitle" })}
          </Paragraph>
        </Typography>
        {userToken?.rights?.indexOf(RIGHTS.maintainAllFormProperties) >= 0 &&
        categories.length ? (
          <Row justify="end" gutter={[8, 8]}>
            <Col>
              <label>
                {intl.formatMessage({ id: "editForm.productCategory" })}:{" "}
              </label>
              <Select
                data-test-id="edit-form-product-category"
                onChange={setProductCategory}
                allowClear
                value={productCategory}
                size={"200px"}
                style={{ minWidth: "200px" }}
                placeholder={"Product Category"}
              >
                {categories.map((i) => (
                  <Option key={i.id} value={i.id}>
                    {intl.formatMessage({
                      id: i.label.id,
                      defaultMessage: i.label.fallback,
                    })}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        ) : (
          ""
        )}
        {ordering ? (
          <OrderableItems
            items={formData}
            idKey={({ property: { id } }) => id}
            nameKey={({
              property: {
                label: { fallback },
              },
            }) => fallback}
            nestedDataKey={({ children }) => children}
            setItems={(sortedFields, parentId) => {
              setFormData(
                updateFormFieldsRecursive(formData, sortedFields, parentId)
              );
            }}
          />
        ) : (
          <Table
            {...tableProps}
            columns={columns}
            pagination={false}
            loading={isFetching}
          />
        )}
      </Container>
    </>
  );
};

EditFormContent.propTypes = {
  productCategory: string,
  setProductCategory: func.isRequired,
  formName: string.isRequired,
};
